import { getPurePlaybookList } from '@Store/feature/purePlaybookSlice';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Icons } from '../../../assets/SvgIcons';
import CustomFilterMenu from '../../../components/UI/CustomFilterMenu/CustomFilterMenu';
import { FilterIProps } from '../../../config/filter';
import { DateRanger } from '../../../config/range';
import { getPlayBookDetail } from '../../../store/feature/playBookDetailSlice';
import { useAppSelector } from '../../../store/hooks';

export interface MergefloDependencyFiltersProps {
  functionalAreasFilter: FilterIProps[];
  setFunctionalAreasFilter: (
    value: React.SetStateAction<FilterIProps[]>
  ) => void;
  statusFilter: FilterIProps[];
  setStatusFilter: (value: React.SetStateAction<FilterIProps[]>) => void;
  rangeFilter: DateRanger;
  setRangeFilter: (value: React.SetStateAction<DateRanger>) => void;
  dueDateFilter: FilterIProps[];
  setDueDateFilter: (value: React.SetStateAction<FilterIProps[]>) => void;
  progressFilter: FilterIProps[];
  setProgressFilter: (value: React.SetStateAction<FilterIProps[]>) => void;
  moreFilter: FilterIProps[];
  setMoreFilter: (value: React.SetStateAction<FilterIProps[]>) => void;
  assigneeFilter: FilterIProps[];
  setAssigneeFilter: (value: React.SetStateAction<FilterIProps[]>) => void;
  likelihoodFilter: FilterIProps[];
  setLikelihoodFilter: (value: React.SetStateAction<FilterIProps[]>) => void;
  setCreateDateFilter: (value: React.SetStateAction<FilterIProps[]>) => void;
  createdDateFilter: FilterIProps[];
  createRangeFilter: DateRanger;
  setCreateRangeFilter: (value: React.SetStateAction<DateRanger>) => void;
  setTargetFunctionalAreaFilter: (
    value: React.SetStateAction<FilterIProps[]>
  ) => void;
  targetFunctionalAreaFilter: FilterIProps[];
}

export default function MergefloDependencyFilters(
  filterProps: MergefloDependencyFiltersProps
) {
  let { id } = useParams();
  const {
    functionalAreasFilter,
    setFunctionalAreasFilter,
    setStatusFilter,
    statusFilter,
    rangeFilter,
    setRangeFilter,
    dueDateFilter,
    moreFilter,
    setDueDateFilter,
    setMoreFilter,
    setAssigneeFilter,
    assigneeFilter,
    createRangeFilter,
    createdDateFilter,
    setCreateDateFilter,
    setCreateRangeFilter,
    targetFunctionalAreaFilter,
    setTargetFunctionalAreaFilter,
  } = filterProps;
  const { userList, dealTaskList } = useAppSelector(getPlayBookDetail);

  const purePlaybooks = useAppSelector(getPurePlaybookList);
  let sourceFunctions = dealTaskList
    .filter(a => a.dependencySource)
    .map(i => i.dependencySource);
  let targetFunctions = dealTaskList
    .filter(a => a.dependencyTarget)
    .map(i => i.dependencyTarget);
  sourceFunctions = Array.from(new Set(sourceFunctions));
  targetFunctions = Array.from(new Set(targetFunctions));

  const sourcesFunctionElements = [];
  for (const item of sourceFunctions) {
    sourcesFunctionElements.push({
      key: item,
      value:
        purePlaybooks?.find(f => f.functionalAreaId === item)?.project
          .taskPrefix ?? '',
    } as FilterIProps);
  }
  const { t } = useTranslation();

  const targetFunctionElements = [];
  for (const item of targetFunctions) {
    targetFunctionElements.push({
      key: item,
      value:
        purePlaybooks?.find(f => f.functionalAreaId === item)?.project
          .taskPrefix ?? '',
    } as FilterIProps);
  }

  const addFunctionalAreaFilter = (data: FilterIProps) => {
    console.log(data);

    if (!functionalAreasFilter.find(s => s.key === data.key)) {
      setFunctionalAreasFilter(old => [...old, data]);
    } else {
      setFunctionalAreasFilter(old => [...old.filter(o => o.key !== data.key)]);
    }
  };

  const addTargetFunctionalAreaFilter = (data: FilterIProps) => {
    console.log(data);

    if (!targetFunctionalAreaFilter.find(s => s.key === data.key)) {
      setTargetFunctionalAreaFilter(old => [...old, data]);
    } else {
      setTargetFunctionalAreaFilter(old => [
        ...old.filter(o => o.key !== data.key),
      ]);
    }
  };

  const addStatus = (data: FilterIProps) => {
    console.log(data);

    if (!statusFilter.find(s => s.key === data.key)) {
      setStatusFilter(old => [...old, data]);
    } else {
      setStatusFilter(old => [...old.filter(o => o.key !== data.key)]);
    }
  };

  const addDueDate = (data: FilterIProps) => {
    setDueDateFilter([data]);
  };

  const addAssigneeFilter = (data: FilterIProps) => {
    console.log(data);

    if (!assigneeFilter.find(s => s.key === data.key)) {
      setAssigneeFilter(old => [...old, data]);
    } else {
      setAssigneeFilter(old => [...old.filter(o => o.key !== data.key)]);
    }
  };

  const addCreatedDate = (data: FilterIProps) => {
    setCreateDateFilter([data]);
  };

  const addMoreFilters = (data: FilterIProps) => {
    if (!moreFilter.find(s => s.key === data.key)) {
      setMoreFilter(old => [...old, data]);
    } else {
      setMoreFilter(old => [...old.filter(o => o.key !== data.key)]);
    }
  };

  const moreFilterProps: Array<FilterIProps> = [
    {
      key: 'hasRisks',
      value: t('dependencies.list_tab.has_risks'),
    },
    {
      key: 'hasIssues',
      value: t('dependencies.list_tab.has_issues'),
    },
    {
      key: 'hasDependencies',
      value: t('dependencies.list_tab.has_dependencies'),
    },
    {
      key: 'hasDecisions',
      value: t('dependencies.list_tab.has_decisions'),
    },
    {
      key: 'uncompleted',
      value: t('dependencies.list_tab.has_uncompleted'),
    },
    {
      key: 'completed',
      value: t('dependencies.list_tab.has_completed'),
    },
  ];

  const userTags: FilterIProps[] = [];
  if (userList) {
    for (const item of userList) {
      if (!userTags.map(u => u.key).includes(item.username)) {
        userTags.push({
          key: item.username,
          value: item.name ? item.name + ' ' + item.surname : item.username,
        } as FilterIProps);
      }
    }
    userTags.push({
      key: '0',
      value: t('dependencies.list_tab.status_unassigned'),
    });
  }
  return (
    <>
      <CustomFilterMenu
        items={[
          {
            key: '1',
            value: 'Overdue',
            htmlValue: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'Inter',
                  color: '#42526E',
                  fontWeight: 600,
                }}
              >
                <div className="titledot" style={{ borderColor: 'red' }}></div>{' '}
                {t('dependencies.list_tab.status_overdue')}
              </div>
            ),
          },
          {
            key: '2',
            value: 'Caution',
            htmlValue: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'Inter',
                  color: '#42526E',
                  fontWeight: 600,
                }}
              >
                <div className="titledot"></div>{' '}
                {t('dependencies.list_tab.status_caution')}
              </div>
            ),
          },
          {
            key: '3',
            value: 'On Track',
            htmlValue: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'Inter',
                  color: '#42526E',
                  fontWeight: 600,
                }}
              >
                <div
                  className="titledot"
                  style={{ borderColor: 'green' }}
                ></div>{' '}
                {t('dependencies.list_tab.status_ontrack')}
              </div>
            ),
          },
          {
            key: '4',
            value: 'Unassigned',
            htmlValue: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'Inter',
                  color: '#42526E',
                  fontWeight: 600,
                }}
              >
                <div
                  className="titledot"
                  style={{ borderColor: '#808080' }}
                ></div>{' '}
                {t('dependencies.list_tab.status_unassigned')}
              </div>
            ),
          },
        ]}
        title={<>{t('dependencies.list_tab.status')}</>}
        clearButton={true}
        type="right-check"
        multiSelect={true}
        handleItemClick={addStatus}
        selectedItems={statusFilter}
        handleClickClear={() => setStatusFilter([])}
      />

      <CustomFilterMenu
        items={[
          {
            key: moment(rangeFilter.selection.endDate).format('MM-DD-YYYY'),
            value:
              'Due Date: ' +
              moment(rangeFilter.selection.endDate).format('MM-DD-YYYY'),
          },
        ]}
        selectedItems={dueDateFilter}
        handleItemClick={addDueDate}
        setRange={setRangeFilter}
        clearButton={true}
        multiSelect={false}
        type="calendar"
        title={<>{t('decisions.due_date')}</>}
        clearTitle={t('decisions.due_date')}
        handleClickClear={() => setDueDateFilter([])}
      />
      <CustomFilterMenu
        items={[
          {
            key: moment(createRangeFilter.selection.endDate).format(
              'YYYY-MM-DD'
            ),
            value:
              'Create Date: ' +
              moment(createRangeFilter.selection.endDate).format('MM-DD-YYYY'),
          },
        ]}
        selectedItems={createdDateFilter}
        handleItemClick={addCreatedDate}
        setRange={setCreateRangeFilter}
        clearButton={true}
        multiSelect={false}
        type="calendar"
        title={<>{t('decisions.create_date')}</>}
        clearTitle="Create Date "
        handleClickClear={() => setCreateDateFilter([])}
      />
      <CustomFilterMenu
        items={userTags}
        clearButton={true}
        multiSelect={true}
        selectedItems={assigneeFilter}
        title={<>{t('dependencies.list_tab.assignee')}</>}
        type="left-check"
        handleItemClick={addAssigneeFilter}
        handleClickClear={() => setAssigneeFilter([])}
      />
      <CustomFilterMenu
        items={sourcesFunctionElements}
        clearButton={true}
        multiSelect={true}
        selectedItems={functionalAreasFilter}
        title={<>{'Source'}</>}
        type="left-check"
        handleItemClick={addFunctionalAreaFilter}
        handleClickClear={() => setFunctionalAreasFilter([])}
      />
      <CustomFilterMenu
        items={targetFunctionElements}
        clearButton={true}
        multiSelect={true}
        selectedItems={targetFunctionalAreaFilter}
        title={<>{t('dependencies.list_tab.target_playbook')}</>}
        type="left-check"
        handleItemClick={addTargetFunctionalAreaFilter}
        handleClickClear={() => setTargetFunctionalAreaFilter([])}
      />
      <CustomFilterMenu
        items={moreFilterProps}
        clearTitle={t('dependencies.list_tab.more_filter2')}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <>{t('dependencies.list_tab.more_filter')}</>
            <Icons.Add />
          </div>
        }
        selectedItems={moreFilter}
        multiSelect={true}
        type="default"
        handleItemClick={addMoreFilters}
        handleClickClear={() => setMoreFilter([])}
        clearButton={false}
      />
    </>
  );
}
